import { useMemo } from "react";

import { IS_AWS_ENV } from "constants/app.constants";
import { EvaChart, RigFracGroupBy } from "constants/charts.enums";
import {
  RIG_FRAC_CHART_GROUP_BY_ATTRIBUTE,
  RIG_FRAC_FIELD
} from "constants/rigFrac.constants";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { RIG_FRAC_PLASMA_PALETTE } from "entities/charts/eva/requests/rigFracColorPalette";
import {
  selectHeldFocusColors,
  useFocusColorHoldStore
} from "stores/focusColorHoldStore";
import type { RigFracColorSource } from "stores/map";
import {
  ChartRequestType,
  RigFracActivityChartRequestType,
  RigFracRecentActivityChartRequestType
} from "types";
import {
  normalizeMatchKey,
  resolveFocusPaint,
  sendsFocusBins,
  shouldApplyFocusColors,
  shouldHoldForPendingFocusColors
} from "utils/color/focusColorSync";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { useFocusColorSync } from "hooks/focusColorSync";
import { useRigFracBasePalette } from "hooks/rigfrac/useRigFracBasePalette";
import useBetaFeatures from "hooks/useBetaFeatures";

import {
  RIG_FRAC_DEFAULT_PALETTE,
  buildRigFracColorAssignment
} from "components/map/utils/rigFracColorAssignment";
import type { RigFracColorByField } from "components/project/layers/components/rig-frac-details/rigFracColorByFields";
import { useRigFracValuesQuery } from "components/project/layers/queries";

// A last-resort fallback for buildRigFracColorAssignment's per-value assignment
// (only reached if the palette itself is unusable) — deliberately NOT the
// shared rig/frac n/a grey (RIG_FRAC_NA_COLOR). That colour means "blank
// operator" on the map and in this hook's own output; it must never also mean
// "colouring failed" for a real, non-blank value.
const CHART_FALLBACK_COLOR = "#000";

// The /values list each dimension's domain is read from. The map colours from
// the same list, and colours go by domain position, so a value gets the same
// hex on both only if the two read the same key.
const RIG_FRAC_CHART_VALUES_KEY: Record<RigFracGroupBy, RigFracColorByField> = {
  [RigFracGroupBy.Operator]: RIG_FRAC_FIELD.Operator,
  [RigFracGroupBy.Basin]: RIG_FRAC_FIELD.Basin,
  [RigFracGroupBy.SubBasin]: RIG_FRAC_FIELD.SubBasin,
  [RigFracGroupBy.Country]: RIG_FRAC_FIELD.Country,
  [RigFracGroupBy.StateProvince]: RIG_FRAC_FIELD.StateProvince,
  [RigFracGroupBy.County]: RIG_FRAC_FIELD.County,
  [RigFracGroupBy.LocationType]: RIG_FRAC_FIELD.LocationType,
  [RigFracGroupBy.RigTrajectory]: RIG_FRAC_FIELD.RigTrajectory
};

// Stable empty answer for a chart that can never take focus colours, so a
// non-rig/frac legend subscribes to a constant rather than to the store's real
// state (see the call site).
const NO_HELD_FOCUS_COLORS = {};
const selectNoHeldFocusColors = () => NO_HELD_FOCUS_COLORS;

// The rig/frac default with its colours reversed, for a user who reverses
// Plasma in the chart's palette picker. Built from the same palette object the
// assignment uses by default, so an unreversed chart and a reversed one differ
// in order only, never in which colours appear.
const REVERSED_RIG_FRAC_DEFAULT_PALETTE = RIG_FRAC_DEFAULT_PALETTE
  ? { ...RIG_FRAC_DEFAULT_PALETTE, reverse: true }
  : RIG_FRAC_DEFAULT_PALETTE;

export interface UseRigFracSeriesColorsParams {
  chartType: EvaChart;
  request: Maybe<ChartRequestType>;
  /**
   * The labels this chart is actually going to paint, from the response. The
   * value domain does not always cover every one of them, and a label it misses
   * would otherwise keep the server's colour, which comes from a different
   * palette and looks like the sync failing. Pass only the series that carry a
   * value of the group-by attribute (isRigFracValueSeries), never the overlay
   * total, and pass the same list from every call site or the legend and the
   * bars will disagree.
   */
  seriesLabels?: Maybe<string[]>;
}

export interface RigFracSeriesColors {
  // normalized series label -> hex. Empty when the chart keeps server colours.
  colors: ReadonlyMap<string, string>;
  isActive: boolean;
}

// Frozen singleton, returned by identity whenever the chart falls back to
// server-resolved colours, so consumer effect dep arrays don't churn.
const EMPTY_RIG_FRAC_SERIES_COLORS: RigFracSeriesColors = Object.freeze({
  colors: new Map<string, string>(),
  isActive: false
});

// Either rig/frac request shape may show up on the chart entity mid chart-type
// switch, so read both leniently (same cast pattern as useRigFracActivityController).
type RigFracColorSelections = Partial<RigFracActivityChartRequestType> &
  Partial<RigFracRecentActivityChartRequestType>;

/**
 * Resolves rig/frac chart series colours client-side, using the same
 * assignment function (`buildRigFracColorAssignment`) and the same focus
 * resolution (`useFocusColorSync`) the map adapter and layer panel use, so
 * the map and the chart agree on a hex by construction.
 *
 * On AWS, every dimension is client-coloured while the chart is on the
 * default palette, from the main focus's palette (EVA-338). On Azure, Operator
 * is client-coloured on the default Plasma palette with or without a focus, to
 * keep the no-focus case in sync with the map (SC-2); any other dimension
 * only joins in while a focus that matches that exact dimension is active.
 *
 * Takes the chart's already-resolved `chartType`/`request` rather than a
 * `chartId` + its own `useChartEntities` call: every consumer already calls
 * `useChartEntities` (or mocks it) itself, so re-reading it here would be a
 * second Redux subscription for the same data, and — because this hook is
 * meant to be called unconditionally by any chart's legend/options hook — a
 * deep import of `useChartEntities` here would bypass a barrel mock
 * (`jest.mock("hooks/charts")`) those callers' own tests rely on.
 */
export function useRigFracSeriesColors(
  params: UseRigFracSeriesColorsParams
): RigFracSeriesColors {
  const { chartType, request, seriesLabels } = params;
  // Depended on as a string: the caller builds this array from the response on
  // every render, so its identity churns even when the labels have not changed.
  const seriesLabelsKey = seriesLabels?.join("\u0000") ?? "";
  const isRigFracChart =
    chartType === EvaChart.RigFracActivity ||
    chartType === EvaChart.RigFracRecentActivity;

  const selections = request as unknown as Maybe<RigFracColorSelections>;
  const groupBy = selections?.groupBy ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy;
  const chartAttribute = RIG_FRAC_CHART_GROUP_BY_ATTRIBUTE[groupBy];

  // Palette discriminator: only the absence of a palette entirely counts as the
  // default (the window before the controller echoes the fully built request
  // back onto the chart entity). A palette that is present but carries no id is
  // a real pick, not the default, so it hands colouring back to the server like
  // any other pick rather than being silently overpainted with Plasma.
  // Picking Plasma explicitly behaves exactly like the default, except on AWS,
  // where the default follows the focus palette and a marked pick stays Plasma.
  const chartPalette = selections?.colorPalette;
  const isDefaultPalette =
    chartPalette == null ||
    (chartPalette.id === RIG_FRAC_PLASMA_PALETTE.id &&
      !(IS_AWS_ENV && chartPalette.userPicked === true));
  const isReversedDefaultPalette = isDefaultPalette && chartPalette?.reverse === true;

  const { hasFeature } = useBetaFeatures();
  const isBetaEnabled = hasFeature("Rig And Frac Layers");

  // AWS: on the default palette every dimension is client-coloured from the
  // main focus's palette, not only Operator or a matching focus (EVA-338).
  const wellPalette = useRigFracBasePalette();
  const followsWellPalette = isDefaultPalette && wellPalette != null;

  // Gated on the chart type, not just the beta flag: this hook is called from
  // every chart's legend, and without the gate the translation request would
  // fire on chart screens that have no rig or frac series to colour.
  const resolution = useFocusColorSync({ enabled: isRigFracChart });

  const gateParams = { resolution, layerAttribute: chartAttribute };

  // Operator never needs a genuine focus match to join the client-coloured
  // set while on the default palette (see the function doc); every other
  // dimension, and Operator on a non-default palette, needs a real match.
  const isOperatorAttribute = chartAttribute === RIG_FRAC_FIELD.Operator;
  const isFocusMatchActive = chartAttribute != null && shouldApplyFocusColors(gateParams);
  const isPendingForChart =
    chartAttribute != null && shouldHoldForPendingFocusColors(gateParams);

  // What each attribute last resolved to. Shared with the map adapter and the
  // layer panel, and keyed by attribute, so this hook's two call sites (a
  // chart's legend and its bars) hold the same colours even when one of them
  // mounts in the middle of a pending window, and a held operator answer can
  // never be served to a chart now grouped by basin.
  //
  // Gated on the chart type for the same reason the legend atom and the filter
  // id are gated upstream: this hook runs from EVERY chart's legend, and an
  // unconditional subscription would re-render every one of them each time a
  // focus resolves, including charts that can never use the value.
  //
  // Read up front (not inside the memo below) because isEligible, on the
  // non-default palette branch, needs to know whether there is a held answer
  // to show rather than just that a pending window is open.
  const heldFocusColors = useFocusColorHoldStore(
    isRigFracChart ? selectHeldFocusColors : selectNoHeldFocusColors
  );
  const held = chartAttribute != null ? heldFocusColors[chartAttribute] : undefined;

  // A non-default palette only joins the client-coloured set once a focus
  // genuinely matches this chart's dimension (active), or a pending window has
  // a held answer to show; pending with nothing held would client-colour from
  // the user's own palette and then repaint again once the focus resolves —
  // three states in a row, which is its own flash on top of the one this hook
  // exists to prevent.
  const isEligible =
    isRigFracChart &&
    (followsWellPalette ||
      (chartAttribute != null &&
        (isDefaultPalette
          ? isOperatorAttribute || isFocusMatchActive
          : isFocusMatchActive || (isPendingForChart && !!held))));

  // Same one-hour, single-shared-cache-entry query the map uses, so this never
  // issues an extra request. Gated on the beta flag too (not just chart type)
  // so a non-beta org can never fire this request even if a leftover rig/frac
  // chart entity sticks around after the flag is turned off for them.
  const values = useRigFracValuesQuery({ enabled: isRigFracChart && isBetaEnabled });

  const valuesKey = RIG_FRAC_CHART_VALUES_KEY[groupBy];

  return useMemo(() => {
    // No domain until /values answers: the server's colours stay up rather than
    // colouring by plot order and repainting once the real positions land.
    const domain = values.data?.values?.[valuesKey];

    // A pending window keeps a chart that is ALREADY client-coloured client-
    // coloured, standing in the held colours until the real ones land: dropping
    // to the empty singleton would make the translators adopt the server's
    // style.hexColor mid-transition and repaint again a moment later, which is
    // the flash this hook exists to remove. Operator qualifies on the default
    // palette because it is client-coloured with or without a focus there; on
    // a non-default palette it needs the same held answer isEligible requires,
    // so an Operator chart on a custom palette does not take this shortcut.
    const isHoldingClientColors =
      isPendingForChart && ((isDefaultPalette && isOperatorAttribute) || !!held);
    if ((!isEligible && !isHoldingClientColors) || !domain || domain.length === 0) {
      return EMPTY_RIG_FRAC_SERIES_COLORS;
    }

    const resolvedPaint = resolveFocusPaint({
      resolution,
      layerAttribute: chartAttribute,
      held
    });
    // With bins sent, every bar a bin names is labelled with the bin, so the
    // bin's colour beats a company name that normalizes the same. Without them
    // the labels are raw values and keep the map's colours.
    const focusPaint =
      resolvedPaint?.binColors?.size && sendsFocusBins(resolvedPaint)
        ? {
            ...resolvedPaint,
            colors: new Map([...resolvedPaint.colors, ...resolvedPaint.binColors])
          }
        : resolvedPaint;
    // The focus's palette outranks defaultPalette, so a reversed default has to
    // reverse it too or unmatched series ignore the reverse. Matched values keep
    // their exact focus colours either way.
    const focus =
      followsWellPalette && isReversedDefaultPalette && focusPaint?.basePalette
        ? {
            ...focusPaint,
            basePalette: {
              ...focusPaint.basePalette,
              reverse: !focusPaint.basePalette.reverse
            }
          }
        : focusPaint;

    // A chart is not scoped to a project layer, and the Rigs and Frac Crews
    // layers can hold conflicting per-value overrides for the same value, so
    // this deliberately passes no overrides — unlike the map adapter and layer
    // panel, which pass their own layer's values. colorSource is the chart's
    // own palette on the non-default path, so unmatched values still spread
    // from the palette the user picked rather than reverting to Plasma; the
    // default path keeps passing undefined, as before.
    const chartColorSource: Maybe<RigFracColorSource> =
      !isDefaultPalette && chartPalette != null
        ? { kind: "palette", palette: chartPalette }
        : undefined;

    const { valueColors } = buildRigFracColorAssignment({
      colorByField: valuesKey,
      domain,
      overrides: {},
      colorSource: chartColorSource,
      staticColor: CHART_FALLBACK_COLOR,
      focus,
      // Reversing the default palette keeps its id, so it is still the default
      // and the chart is still coloured here rather than by the server. Without
      // this the reverse would do nothing at all on the bars while the map's
      // own palette control reverses, which is the two surfaces disagreeing.
      defaultPalette: followsWellPalette
        ? isReversedDefaultPalette
          ? { ...wellPalette, reverse: !wellPalette.reverse }
          : wellPalette
        : isReversedDefaultPalette
        ? REVERSED_RIG_FRAC_DEFAULT_PALETTE
        : undefined,
      // Anything this chart plots that the value domain does not list still has
      // to be coloured from the same palette. Left out, it keeps the server's
      // colour and shows up as the one bar painted from a different palette.
      // Others is the exception: it must never take a palette slot.
      extraValues: seriesLabels?.filter((label) => !isOthersGroupLabel(label)),
      // The chart always keeps the shared n/a grey for a blank value, even on
      // the non-default path where chartColorSource is the user's own
      // (non-null) palette — unlike the map/panel, which let a real per-layer
      // colorSource paint a blank value like any other.
      blankTakesNaColor: true
    });

    // Re-key by normalized label: chart series labels come straight from
    // chart-svc and can differ in case/whitespace from the /values domain.
    const colors = new Map<string, string>();
    valueColors.forEach((color, value) => {
      colors.set(normalizeMatchKey(value), color);
    });
    // Without a focus Others colour, Others keeps the server's grey.
    const othersColor = focus?.othersColor;
    if (othersColor) {
      seriesLabels?.filter(isOthersGroupLabel).forEach((label) => {
        colors.set(normalizeMatchKey(label), othersColor);
      });
    }

    return { colors, isActive: true };
  }, [
    isPendingForChart,
    isOperatorAttribute,
    isEligible,
    followsWellPalette,
    wellPalette,
    isReversedDefaultPalette,
    isDefaultPalette,
    chartPalette,
    chartAttribute,
    valuesKey,
    values.data,
    held,
    resolution,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    seriesLabelsKey
  ]);
}
